import type { Copy } from './types';

export const en: Copy = {
  nav: {
    about: 'About us',
    apiPlatform: 'API Platform',
    login: 'Sign in',
    cta: 'Talk to us',
  },
  hero: {
    title: 'be the jungle.',
    subA: 'The single intelligent platform that connects it all.',
    subB: 'The definitive system to run your agency.',
    cta: 'Get started',
  },
  aboutHero: {
    title: "Building the jungle\nof tomorrow's travel",
    subA: 'We are a team passionate about transforming travel distribution through technology and data.',
    subB: 'Our mission: help agencies win.',
    cta: 'Talk to us',
  },
  apiPlatformHero: {
    title: 'One Connection.\nInfinite Opportunities.',
    subA: 'Connect global suppliers and distributors through one powerful hub — ship faster, sell smarter, and simplify your travel business.',
    subB: 'Enterprise travel API suite for product teams.',
    cta: 'Request access',
    docsCta: 'View API documentation',
    microLine: 'API-first · Full documentation · Sandbox included',
  },
  sector: {
    eyebrow: 'THE TRAVEL JUNGLE',
    title: 'Our industry is getting wilder',
    lead: 'Dozens of suppliers, fragmented systems, prices that change constantly, deadlines, errors — and agencies trying to survive a complex ecosystem to find the best options for their clients.',
  },
  sobrevivir: {
    eyebrow: 'THE INFRASTRUCTURE THAT CONNECTS IT ALL',
    titleA: 'Stop surviving.',
    titleB: 'Be the jungle.',
    lead: 'One intelligent platform that unifies your suppliers, automates your proposals and maximises your sales. Multi-supplier, multi-product, multi-destination.',
  },
  adapta: {
    eyebrow: 'WITHOUT CHANGING YOUR SUPPLIER ECOSYSTEM',
    titleA: 'Bundleport adapts',
    titleB: 'to you, not the other way around',
    lead: 'Get started in days, not months. Our guided onboarding connects you with your current suppliers and helps you grow your inventory at no extra cost.',
  },
  proveedores: {
    eyebrow: 'ALL SPECIES. ONE ECOSYSTEM.',
    titleA: 'All your suppliers,',
    titleB: 'in one place',
  },
  creces: {
    eyebrow: 'PRICING',
    titleA: 'You grow,',
    titleB: 'we grow',
    lead: 'A business model aligned with yours.\nNo fine print, no minimum commitment.',
    cta: 'Contact sales',
  },
  contacto: {
    eyebrow: 'CONTACT',
    titleA: 'Stop surviving.',
    titleB: 'Be the jungle.',
    formTitle: 'Request your demo',
    sideTitle: "Let's talk about your agency",
    sideLead:
      'Tell us how you work today and we will show you how to sell more, operate less and improve your margin. No commitment.',
    submit: 'Request demo',
    sending: 'Sending…',
    sent: 'Sent! Thank you',
    errorMsg: 'Error. Please try again.',
    consent:
      'I accept the <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a> and agree that Bundleport may use my data to contact me about this request and related products.',
    consentError: 'You must accept the privacy policy to continue.',
    motorLabel: 'Do you use your own booking engine or an external solution?',
    motorOwn: 'Own',
    motorExternal: 'External',
    motorWhich: 'Which one?',
    motorWhichPh: 'Name of the engine or solution',
    forLabel: 'For',
  },
  aboutPage: {
    missionLabel: 'Our mission',
    missionTitle: 'Giving power back<br />to travel agencies',
    missionP1:
      'The travel industry moves fast. Agents need technology that frees them from complex operations and lets them focus on what truly matters: creating memorable travel experiences.',
    missionP2:
      "We believe travel agencies have irreplaceable value: trust, knowledge and personal relationships with travelers. Our technology amplifies that value, it doesn't replace it.",
    missionP3:
      'We are an AI-native distribution platform that unifies suppliers, automates processes and equips agencies with the tools to operate efficiently, scale without friction and stand out in the market.',
    missionTagline: 'All Species. One Ecosystem',
    valuesLabel: 'Our values',
    valuesTitle: 'What guides us every day',
    values: [
      {
        icon: '🤝',
        title: 'Be a Partner',
        desc: 'We grow stronger together. We build transparent, win-win relationships that create lasting value for everyone involved in the ecosystem.',
      },
      {
        icon: '🔒',
        title: 'Be Reliable',
        desc: 'We keep our promises. We deliver secure, consistent and scalable solutions so our partners can count on us every step of the way.',
      },
      {
        icon: '🌱',
        title: 'Be a Good Persona',
        desc: 'We believe in doing good while doing business. We act with integrity, empathy and respect in every interaction — inside and outside the company.',
      },
      {
        icon: '⚡',
        title: 'Be Empowering',
        desc: 'We help others shine. We put our partners in control, giving them clarity, confidence and the tools to succeed.',
      },
      {
        icon: '🚀',
        title: 'Be Innovative with Purpose',
        desc: "We solve real problems with creativity. We build solutions that simplify, connect and empower — not just technology for technology's sake.",
      },
    ],
    commitmentLabel: 'Our commitment',
    commitmentText:
      "Every day, we work to live these values and create a travel technology ecosystem where partnerships flourish, innovation thrives and everyone has the tools they need to succeed. We're not just building technology — we're building relationships and empowering the future of travel.",
    ctaTitle: 'Ready to<br />join the jungle?',
    ctaSub: 'Talk to our team and discover how Bundleport can transform your agency.',
  },
  apiPlatformPage: {
    whyBpLabel: 'Why Bundleport',
    whyBpTitle: 'Modern travel tech, no legacy baggage',
    whyBpItem1:
      'A multi-product <strong>AI-native</strong> stack vs. legacy point-to-point integrations.',
    whyBpItem2: '<strong>One single API</strong> vs. dozens of fragile supplier connections.',
    whyBpItem3:
      '<strong>Sandbox and documentation</strong> built for teams that want real deployment speed.',
    perfectForLabel: 'Designed for',
    perfectForTitle: 'Companies demanding full control and flexibility',
    perfectForDesc:
      'Bundleport Connect is built for tech teams who want to integrate travel without friction.',
    audiences: [
      {
        icon: '🚀',
        title: 'Tech Startups',
        desc: 'Build innovative travel experiences with flexible APIs and fast deployment. Launch in days, not months.',
      },
      {
        icon: '🏢',
        title: 'Enterprise Travel',
        desc: 'Integrate travel capabilities into existing platforms with enterprise-grade reliability and SLA-backed uptime.',
      },
      {
        icon: '🔗',
        title: 'System Integrators',
        desc: 'Connect multiple systems seamlessly with comprehensive documentation and dedicated support.',
      },
    ],
    whyLabel: 'Why Bundleport Connect',
    whyTitle: 'One connection.<br />All global inventory.',
    whyDesc:
      "The travel industry doesn't need more complexity — it needs clarity. Replace multiple integrations with one streamlined hub that does it all.",
    features: [
      {
        icon: '🌐',
        title: 'Unified global inventory',
        desc: 'Flights, hotels, experiences and trains through one secure API. No separate contracts per supplier.',
      },
      {
        icon: '⚡',
        title: 'Real-time data access',
        desc: 'Live pricing, availability and booking with <500ms response times. Never stale data.',
      },
      {
        icon: '🛠️',
        title: 'Flexible, modular integration',
        desc: 'Connect to your CRM, CMS or custom back-office with plug-and-play modules. Adapt what you need.',
      },
      {
        icon: '📄',
        title: 'Developer-ready environment',
        desc: 'Rich docs, sandbox testing and quick-start templates for faster builds.',
      },
      {
        icon: '🛡️',
        title: 'Enterprise reliability',
        desc: 'SLA-backed uptime, proactive monitoring and dedicated technical support for mission-critical operations.',
      },
    ],
    whyCardLabel: 'Real results',
    whyCardTitle: 'The platform that eliminates technical complexity from the travel industry',
    stats: [
      { value: '+20', label: 'Certified integrated suppliers' },
      { value: '<500ms', label: 'Guaranteed response time' },
      { value: '99.9%', label: 'SLA-guaranteed uptime' },
      { value: '1 API', label: 'For all inventory' },
    ],
    supplyLabel: 'Direct benefits',
    supplyTitle: 'More supply.<br />Less work.',
    supplyDesc:
      'For agencies, OTAs and distributors ready to scale their offering without technical headaches.',
    benefits: [
      {
        icon: '📦',
        title: 'Massive inventory, one API',
        desc: 'Flights, hotels, trains, tours, activities and transfers — all unlocked at once. No supplier negotiations needed.',
      },
      {
        icon: '🚀',
        title: 'Launch in days, not months',
        desc: 'New destinations and products available on your platform in days. Real competitive speed.',
      },
      {
        icon: '💰',
        title: 'Lower costs, higher margins',
        desc: 'Reduce integration spend and focus budget on growth. One connection replaces dozens.',
      },
      {
        icon: '📊',
        title: 'Actionable insights',
        desc: 'Track performance, demand trends and partner reliability in real time. Decisions backed by data.',
      },
    ],
    providersLabel: 'Connected ecosystem',
    providersTitle: 'All Species. One Ecosystem',
    providersDesc: 'Access inventory from top global suppliers through a single integration.',
    providersNote: 'And we keep growing. New providers are integrated constantly.',
    howLabel: 'Simple and fast',
    howTitle: 'Three steps to transform<br />your travel business',
    howDesc: 'Connect once and access everything. No unnecessary technical complexity.',
    steps: [
      {
        num: '1',
        title: 'Connect',
        desc: 'A single integration with Bundleport Connect gives you access to the full ecosystem of global suppliers and distributors.',
      },
      {
        num: '2',
        title: 'Select',
        desc: 'Choose the suppliers or distributors that fit your business model. Set pricing rules, visibility and margin controls.',
      },
      {
        num: '3',
        title: 'Scale',
        desc: 'Start selling and growing immediately. Add new suppliers or markets whenever you want, without redesigning your architecture.',
      },
    ],
    ctaTitle: 'Start building today.<br />The future of travel awaits.',
    ctaSub: 'Join the teams building the future of travel with Bundleport Connect.',
    ctaBtn1: 'Request access',
    ctaBtn2: 'View API documentation',
    ctaMicro: 'Free sandbox · Full documentation',
    contactLabel: 'Contact',
    contactTitle: "Let's talk about your integration",
    contactDesc:
      "Tell us your use case and we'll show you how Bundleport Connect can accelerate your development. No commitment.",
  },
  privacyPage: {
    title: 'Privacy Policy',
    meta: 'Last updated: March 6, 2026 · Bundleport',
    toc: 'Contents',
    intro:
      'At Bundleport we handle your information with transparency and responsibility. This policy explains what data we collect, how we use it and how we protect it.',
    sections: [
      {
        id: 'data-collected',
        num: '1',
        title: 'Information We Collect',
        body: '<p>We collect the following types of information when you use our services:</p><ul><li><strong>Account information:</strong> name, email address, company name and contact details.</li><li><strong>API usage data and logs:</strong> endpoints called, request/response metadata, error rates.</li><li><strong>Communication preferences:</strong> marketing and notification settings.</li><li><strong>Technical information:</strong> IP addresses, browser type, operating system and session identifiers.</li></ul>',
      },
      {
        id: 'how-we-use',
        num: '2',
        title: 'How We Use Your Information',
        body: '<p>We use the information we collect for the following purposes:</p><ul><li>Process API requests and deliver the Service.</li><li>Monitor platform performance and improve reliability.</li><li>Provide customer support and respond to enquiries.</li><li>Send product updates, security notices and promotional communications (with consent).</li><li>Respond to support requests and other enquiries.</li></ul>',
      },
      {
        id: 'security',
        num: '3',
        title: 'Data Security',
        body: '<p>We protect your information through end-to-end encryption, regular security audits, strict access controls and secure data centres. Our team follows industry best practices to safeguard your data.</p>',
      },
      {
        id: 'sharing',
        num: '4',
        title: 'Data Sharing',
        body: '<p>We do not sell, trade or rent your personal information to third parties. Data transfers are conducted only as described in this Privacy Policy and in compliance with applicable law.</p>',
      },
    ],
    rightsTitle: 'Your Rights',
    rightsIntro:
      'You have the right to manage your personal data. You can exercise the following rights by contacting us through our support channels:',
    rights: [
      { title: '🔍 Access', desc: 'Request a copy of the personal data we hold about you.' },
      { title: '✏️ Correction', desc: 'Request correction of inaccurate or incomplete data.' },
      { title: '🗑️ Deletion', desc: 'Request deletion of your personal data where applicable.' },
      {
        title: '📦 Portability',
        desc: 'Receive your data in a structured, machine-readable format.',
      },
    ],
    contactTitle: 'Contact Us',
    contactBody:
      'For any questions about this Privacy Policy or to exercise your rights, please reach out through our support channels.',
  },
  termsPage: {
    title: 'Terms of Service',
    meta: 'Last updated: March 6, 2026 · Bundleport',
    toc: 'Contents',
    intro:
      'By accessing or using the Bundleport platform, you agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and Bundleport.',
    sections: [
      {
        id: 'acceptance',
        num: '1',
        title: 'Acceptance of Terms',
        body: '<p>By accessing or using the Bundleport platform, you agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and Bundleport. If you are using the Service on behalf of an organization, you represent and warrant that you have authority to bind that organization to these Terms.</p>',
      },
      {
        id: 'definitions',
        num: '2',
        title: 'Definitions',
        body: '<p>In these Terms, the following terms have the meanings set out below:</p><ul><li><strong>"Service"</strong> means the Bundleport platform, API, dashboard and all related tools.</li><li><strong>"User"</strong> means any individual or entity that accesses or uses the Service.</li><li><strong>"Content"</strong> means data, information and materials processed through the Service.</li><li><strong>"API"</strong> means the Bundleport Connect application programming interface.</li><li><strong>"Supplier"</strong> means any third-party travel content or inventory provider integrated with the Service.</li></ul>',
      },
      {
        id: 'license',
        num: '3',
        title: 'License and Use of Materials',
        body: '<h3>3.1 Limited licence</h3><p>We grant you a limited, non-exclusive, non-transferable, revocable licence to access and use the Service solely for your internal business purposes in the travel distribution sector.</p><h3>3.2 Prohibited acts</h3><p>You may not: (a) reverse engineer, decompile or disassemble the Service; (b) sublicense, sell or redistribute access to the Service without written authorisation; (c) scrape or harvest data beyond permitted API usage; (d) remove or alter proprietary notices; (e) use the Service for unlawful purposes.</p>',
      },
      {
        id: 'obligations',
        num: '4',
        title: 'User Obligations and Acceptable Use',
        body: "<h3>4.1 Obligations</h3><p>You agree to: provide accurate and complete account information; maintain the security of your credentials and API keys; comply with all applicable laws and regulations; notify us promptly of any security breach.</p><h3>4.2 Misuse</h3><p>You must not use the Service to: circumvent rate limits or technical restrictions; interfere with or disrupt the Service; access another user's account without authorisation; process fraudulent bookings or transactions.</p>",
      },
      {
        id: 'api',
        num: '5',
        title: 'API Usage; Rate Limits; Integration',
        body: '<p>Access to the Bundleport Connect API is subject to rate limits specified in your subscription plan. Exceeding rate limits may result in temporary throttling or suspension of access. You must use the API in accordance with our documentation.</p><p>Cached availability and pricing data may not be stored beyond 24 hours without express written permission.</p>',
      },
      {
        id: 'availability',
        num: '6',
        title: 'Service Availability; Maintenance',
        body: "<p>Bundleport targets 99.9% platform uptime, excluding scheduled maintenance. We will endeavour to provide advance notice of planned maintenance.</p><p>We do not guarantee the availability of third-party supplier content, which is subject to each supplier's own systems and policies.</p>",
      },
      {
        id: 'payments',
        num: '7',
        title: 'Payments; Billing',
        body: "<p>Fees are as specified in your commercial agreement with Bundleport. Unless otherwise agreed, invoices are issued monthly or annually in euros. Late payments may result in suspension of access after a 15-day grace period.</p><p>Prices are subject to change with 30 days' written notice. All fees are exclusive of applicable taxes.</p>",
      },
      {
        id: 'data-protection',
        num: '8',
        title: 'Data Protection and Privacy',
        body: '<p>Each party agrees to comply with applicable data protection laws. Bundleport processes personal data in accordance with our Privacy Policy. Where Bundleport processes traveller personal data on your behalf, it does so as a data processor under a Data Processing Agreement.</p><p>You are responsible for obtaining all necessary consents from travellers whose data you submit to the Service.</p>',
      },
      {
        id: 'third-party',
        num: '9',
        title: 'Third-Party Content; Supplier Services',
        body: "<p>The Service aggregates inventory, pricing and content from third-party suppliers. Bundleport does not guarantee the accuracy, completeness or availability of supplier content.</p><p>Bookings made through the Service are subject to each supplier's terms and conditions, which take precedence over these Terms in relation to the travel service itself.</p>",
      },
      {
        id: 'warranties',
        num: '10',
        title: 'Warranties; Disclaimers',
        body: '<p>The Service is provided on an "as-is" and "as-available" basis. To the maximum extent permitted by law, Bundleport disclaims all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose and non-infringement.</p><p>We do not warrant that the Service will be uninterrupted, error-free or free from harmful components.</p>',
      },
      {
        id: 'liability',
        num: '11',
        title: 'Limitation of Liability',
        body: "<p>To the maximum extent permitted by applicable law, Bundleport's total cumulative liability to you for any claims arising under or related to these Terms shall not exceed the greater of: (a) the total fees paid by you to Bundleport in the twelve (12) months preceding the claim, or (b) €100.</p><p>In no event shall Bundleport be liable for indirect, incidental, consequential or punitive damages, including loss of profits, data or goodwill.</p>",
      },
      {
        id: 'indemnification',
        num: '12',
        title: 'Indemnification',
        body: '<p>You agree to indemnify, defend and hold harmless Bundleport, its affiliates, officers and employees from and against any claims, liabilities, damages and expenses (including reasonable legal fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) any content you submit to the Service.</p>',
      },
      {
        id: 'termination',
        num: '13',
        title: 'Termination; Suspension',
        body: '<p>Either party may terminate this agreement by providing written notice. Bundleport may suspend or terminate your access immediately if you breach these Terms, engage in fraudulent activity, or if your account poses a risk to the Service or other users.</p><p>Upon termination, all licences granted hereunder shall cease immediately.</p>',
      },
      {
        id: 'changes',
        num: '14',
        title: 'Changes to Terms',
        body: '<p>Bundleport reserves the right to modify these Terms at any time. Material changes will be communicated at least 30 days in advance via email or platform notification. Your continued use of the Service after the effective date of any change constitutes acceptance of the updated Terms.</p>',
      },
      {
        id: 'law',
        num: '15',
        title: 'Governing Law; Dispute Resolution',
        body: '<p>These Terms are governed by the laws of Spain. Any dispute arising in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Madrid, Spain, with each party waiving any objection to the venue or jurisdiction of such courts.</p>',
      },
      {
        id: 'misc',
        num: '16',
        title: 'Miscellaneous',
        body: '<p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. These Terms constitute the entire agreement between the parties with respect to the Service.</p><p>Any failure to enforce a right or provision shall not constitute a waiver. You may not assign your rights under these Terms without our prior written consent.</p>',
      },
    ],
  },
};
